/**
 * Lazy-loads iframes marked with data-src (see lpl_add_lazy_loading_to_iframes()
 * in functions.php) by swapping in the real src only once the iframe is
 * actually visible in the viewport. This keeps autoplaying embeds (e.g.
 * Wistia videos with autoplay enabled) from starting playback several
 * seconds before a visitor scrolls them into view.
 *
 * It also handles two extra Wistia-specific gains:
 * - Preconnects to Wistia's domains as soon as a lazy embed is found on the
 *   page, so the connection is already warm by the time the real request
 *   fires (faster load, without doing the actual heavy fetch early).
 * - Injects the Wistia external player script (E-v1.js) on demand, right
 *   when a Wistia iframe's src is set, instead of loading it eagerly on
 *   every page. That third-party script is only needed for advanced player
 *   features, so deferring it keeps its cost off the timeline for visitors
 *   who never scroll to the video.
 *
 * Each iframe also gets a "lazy-iframe" class (styled in
 * sass/components/_animations.scss) that fades it in via CSS once its
 * `load` event fires, so the embed doesn't just pop in abruptly.
 *
 * Runs based on document.readyState instead of only listening for
 * "DOMContentLoaded" — JS/cache optimization plugins (e.g. Autoptimize)
 * can defer or move script execution to after that event has already
 * fired, in which case a plain addEventListener would never run and the
 * embed would be stuck showing nothing.
 */
(function () {
	function init() {
		const lazyIframes = document.querySelectorAll('iframe[data-src]');

		if (!lazyIframes.length) {
			return;
		}

		let wistiaScriptRequested = false;

		function isWistiaSrc(src) {
			return /wistia/i.test(src);
		}

		function loadWistiaScript() {
			if (wistiaScriptRequested) {
				return;
			}
			wistiaScriptRequested = true;

			const scriptUrl = (window.lplWistiaConfig && window.lplWistiaConfig.scriptUrl) ||
				'https://fast.wistia.net/assets/external/E-v1.js';

			if (document.querySelector('script[src="' + scriptUrl + '"]')) {
				return;
			}

			const script = document.createElement('script');
			script.src = scriptUrl;
			script.async = true;
			document.body.appendChild(script);
		}

		function preconnect(href) {
			if (document.querySelector('link[rel="preconnect"][href="' + href + '"]')) {
				return;
			}

			const link = document.createElement('link');
			link.rel = 'preconnect';
			link.href = href;
			document.head.appendChild(link);
		}

		function loadIframe(iframe) {
			const src = iframe.getAttribute('data-src');

			if (!src) {
				return;
			}

			iframe.addEventListener('load', function () {
				iframe.classList.add('is-loaded');
			}, { once: true });

			iframe.setAttribute('src', src);
			iframe.removeAttribute('data-src');

			if (isWistiaSrc(src)) {
				loadWistiaScript();
			}
		}

		lazyIframes.forEach(function (iframe) {
			iframe.classList.add('lazy-iframe');
		});

		const hasWistiaEmbed = Array.prototype.some.call(lazyIframes, function (iframe) {
			return isWistiaSrc(iframe.getAttribute('data-src') || '');
		});

		if (hasWistiaEmbed) {
			preconnect('https://fast.wistia.net');
			preconnect('https://fast.wistia.com');
		}

		if (!('IntersectionObserver' in window)) {
			lazyIframes.forEach(loadIframe);
			return;
		}

		const observer = new IntersectionObserver(function (entries, obs) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) {
					return;
				}

				loadIframe(entry.target);
				obs.unobserve(entry.target);
			});
		}, {
			rootMargin: '0px',
			threshold: 0.25,
		});

		lazyIframes.forEach(function (iframe) {
			observer.observe(iframe);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
